import { FC, useMemo, useState } from 'react'
import cn from './index.module.css'
import { RoundedStarFilled, RoundedStarOutlined } from '@/utils/icons'
import { addCourseToFavorite } from '@pages/CoursesCatalog/tabs/MyCoursesTab/utils/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { declOfNum } from '@/helpers/declOfNum'
import { formateDate } from '@/helpers/date'
import classNames from 'classnames'
import { selectProfile } from '@store/profile.reducer'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import ModalBody from '@pages/Payment/modalBody'
import Modal from '@common/Modal'
import TooltipWrapper from '@pages/Admin/CourseSettings/components/TooltipWrapper/TooltipWrapper'
import { useGetSettings } from '@pages/CoursesCatalog/tabs/CatalogCoursesTab/hooks/getPlatformSettings'
import { useHistory } from 'react-router-dom'
import { useLocation } from 'react-router-dom'

interface IMyCourseCard {
  order: ICourseOrder
  onAddToFavorite?: (order: ICourseOrder) => void
  noBuy?: boolean,
  isPaymentWithoutRegistration?: boolean,
  paymentChildrenId?: number
}

type ModalType = 'group' | 'private' | 'buy'

type ModalConfig = {
  type: ModalType
  data: ICourseOrder
}

const MyCourseCard: FC<IMyCourseCard> = ({
  order,
  noBuy = false,
  onAddToFavorite,
  isPaymentWithoutRegistration = false,
  paymentChildrenId = null
}) => {
  const location = useLocation()
  const history = useHistory()
  const addAlert = useAddAlert()
  const { checkSettings } = useGetSettings()
  const isDisabledExtensionTraining = checkSettings(
    'isDisableExtensionTrainingMyCourses',
  )
  const unpaidText = useSelector((store: any) => {
    return store.settings.isCustomUnpaidCourseStatusText === '1'
      ? store.settings.custom.customUnpaidCourseStatusText
      : null
  })

  const userRole = useSelector(selectProfile).role as string
  const [addToFavoriteLoading, setAddToFavoriteLoading] = useState(false)
  const [modal, setModal] = useState<ModalConfig | null>(null)
  const course = order.direction

  const modalConfig: Record<string, ModalConfig> = {
    group: {
      type: 'group',
      data: order,
    },
    private: {
      type: 'private',
      data: order,
    },
    buy: {
      type: 'buy',
      data: order,
    },
  }

  const getIcon = (course: ICourse): string | undefined => {
    const path = course?.mediaIcon?.path ?? course?.icon_path ?? undefined
    if (!path) return path
    if (path?.startsWith('blob')) {
      return path
    }
    return process.env.REACT_APP_PATH_FILE + path
  }

  function isPastDate(dateString: string): boolean {
    try {
      const inputDate = new Date(dateString)
      const now = new Date()

      if (isNaN(inputDate.getTime())) return false

      return inputDate < now
    } catch (error) {
      return false
    }
  }

  const { isExpired, isFree, isDemo, dateTitle, dateString, onPaymentClick } =
    useMemo(() => {
      const userClass = order.class?.classUser?.find(
        (item) => item?.user_id === order.children_id && !item?.deleted_at,
      )

      const amount =
        userClass?.amount === null ? 0 : Number(userClass?.amount ?? -1)
      const isFree = order.class ? amount === 0 : order.tariff?.type === 'free'
      const isExpired = isFree
        ? false
        : order.class
        ? !userClass?.status_payment ||
          isPastDate(userClass.next_payment_date ?? '')
        : !order?.status_payment || isPastDate(order.next_payment_date ?? '')

      let dateTitle: string | null | undefined
      if (!isFree) {
        if (order.class || order.tariff?.type === 'month') {
          dateTitle = isExpired ? 'не оплачено до:' : 'оплачено до:'
        } else if (order.tariff?.type === 'stream_one_time') {
          dateTitle = null
        } else if (order?.payment_date === null && order?.next_payment_date) {
          dateTitle = 'оплачено до:'
        } else {
          dateTitle = isExpired ? 'не оплачено:' : 'оплачено:'
        }
      }

      let dateString: string | null = null
      if (isFree) {
        dateString = 'Бесплатный'
      } else if (order.class) {
        const date = new Date(userClass?.next_payment_date ?? new Date())
        dateString = formateDate(date, 'DD.MM.YYYY')
      } else if (order.tariff?.type === 'month') {
        const date = new Date(
          order?.next_payment_date ?? new Date(order?.created_at + 1000),
        )
        dateString = formateDate(date, 'DD.MM.YYYY')
      } else if (order.tariff?.type === 'stream_one_time') {
        dateString = isExpired ? 'не оплачено' : 'оплачено'
      } else if (order.tariff?.type === 'hour') {
        const hours = Math.round(order.buy_hours! * 100) / 100
        dateString = `${hours} ${declOfNum(hours, ['час', 'часа', 'часов'])}`
      } else if (order.tariff?.type === 'minute') {
        dateString = `${order.buy_hours ?? 0} минут`
      }
      // if (order.tariff?.type === 'stream_custom_period') {
      //   const months = Math.round(order.buy_months! * 100) / 100
      //   dateString = `${months} ${declOfNum(months, [
      //     'месяц',
      //     'месяца',
      //     'месяцев',
      //   ])}`
      // }
      // if (order.tariff?.type === 'stream_custom_days') {
      //   const days = order.buy_days ?? 0
      //   dateString = `${days} ${declOfNum(days, ['день', 'дня', 'дней'])}`
      // }

      if (order?.payment_date === null && order?.next_payment_date) {
        try {
          dateString =
            formateDate(
              new Date(order?.next_payment_date) ??
                new Date(order?.created_at + 1000),
              'DD.MM.YYYY',
            ) + ' (демо)'
        } catch (error) {
          console.log('error demo days', error)
        }
      }

      const onPaymentClick = () => {
        if (location.pathname !== '/payment' && !isPaymentWithoutRegistration) {
          history.push('/payment')
        }

        const modalType =
          isExpired && !noBuy
            ? 'buy'
            : order?.tariff?.type === 'month'
            ? 'group'
            : 'private'
        setModal(modalConfig[modalType] ?? null)
      }

      const isDemo = order?.payment_date === null && order?.next_payment_date

      if (!isFree && !isDemo && !order.payment_date && unpaidText) {
        dateString = unpaidText
      }

      if (
        ['stream_custom_days', 'stream_custom_period', 'stream_custom_weeks']?.includes(
          order.tariff?.type,
        ) &&
        order?.next_payment_date &&
        !isDemo
      ) {
        dateTitle = 'оплачено до:'
        dateString = order?.next_payment_date
      }

      return {
        isExpired,
        isFree,
        isDemo,
        dateTitle,
        dateString,
        onPaymentClick,
      }
    }, [order])

  const onFavoriteClick = async () => {
    if (addToFavoriteLoading) return
    setAddToFavoriteLoading(true)
    const res = await addCourseToFavorite(order?.direction_id)
    if (res.success) {
      onAddToFavorite?.(order)

      const successMessage =
        order.favorite === 1
          ? 'Курс убран из избранного!'
          : 'Курс добавлен в избранное!'

      addAlert({ title: successMessage })
    } else {
      addAlert({
        type: 'error',
        title: 'Возникла ошибка при добавлении курса в избранное!',
      })
    }
    setAddToFavoriteLoading(false)
  }

  const linkCourse = ['parent', 'children'].includes(userRole)
    ? !!order?.access_deny
      ? '/payment'
      : order?.group_id
      ? `/v2/courses/my/course-or-group-${order?.group_id}`
      : `/v2/courses/my/course-or-group-${order.direction_id}-private`
    : `/courses-check/${order.direction_id}`

  const closeModal = () => {
    setModal(null)
  }

  const isExceptionable = (): boolean => {
    if (isDisabledExtensionTraining) return false
    if (!isExpired && order.tariff?.type === 'stream_one_time') return false
    if (!isFree) return true
    return false
  }

  const isUndistributed = useMemo(() => {
    if (
      [
        'free',
        'month',
        'stream_custom_days',
        'stream_custom_period',
        'stream_custom_weeks',
        'stream_one_time',
      ].includes(order?.tariff?.type)
    ) {
      return (order?.distribution === 0 && !order?.group_id)
    }
    return false
  }, [order])

  return (
    <>
      <div className={cn.cardWrapper}>
        <div className={cn.cardHeader}>
          <div className={cn.nameRow}>{course?.name ?? ''}</div>
          {userRole === 'children' &&
            onAddToFavorite &&
            (order?.favorite === 1 ? (
              <RoundedStarFilled
                className={cn.cardStar}
                onClick={onFavoriteClick}
              />
            ) : (
              <RoundedStarOutlined
                className={cn.cardStar}
                onClick={onFavoriteClick}
              />
            ))}
        </div>
        <div className={cn.content}>
          <div className={cn.iconRow}>
            <div className={cn.dateWrapper}>
              {(userRole === 'children' || isPaymentWithoutRegistration) && (
                <>
                  {dateTitle && <div className={cn.dateTitle}>{dateTitle}</div>}
                  {dateString && (
                    <div
                      className={classNames(
                        cn.dateString,
                        isExpired && cn.alertTextColor,
                      )}
                    >
                      {dateString}
                    </div>
                  )}
                  {isUndistributed && (
                    <div
                      className={classNames(
                        cn.undistributedString,
                        cn.alertTextColor,
                      )}
                    >
                      Вы не распределены в группу. Обратитесь к менеджеру
                    </div>
                  )}
                </>
              )}
            </div>
            <img className={cn.icon} src={getIcon(course)} />
          </div>
        </div>
        <div className={cn.actions}>
          {(!['parent', 'children'].includes(userRole) &&
            !isPaymentWithoutRegistration) && (
            <Link
              to={linkCourse}
              className={classNames(cn.button, cn.primaryButton)}
            >
              Перейти
            </Link>
          )}
          {(['parent', 'children'].includes(userRole) ||
            isPaymentWithoutRegistration) && (
            <>
              {!isExpired && (order.distribution !== 0 || !!order?.group_id) &&
              !isPaymentWithoutRegistration && (
                <Link to={linkCourse} className={cn.button}>
                  Перейти
                </Link>
              )}
              {isPaymentWithoutRegistration && (
                <div className={cn.margin}></div>
              )}
              {isExceptionable() && (
                <TooltipWrapper
                  title={
                    order?.class &&
                    'Этот курс входит в класс, если вы оплатите его, то продлятся все курсы, входящие в этот класс'
                  }
                  style={{ width: '100%' }}
                >
                  <button
                    className={classNames(
                      cn.button,
                      isExpired ? cn.extendButton : cn.primaryButton,
                    )}
                    onClick={onPaymentClick}
                  >
                    {!isExpired ? 'Продлить обучение' : 'Оплатить обучение'}
                  </button>
                </TooltipWrapper>
              )}
            </>
          )}
        </div>
      </div>
      {/* @ts-ignore */}
      <Modal active={!!modal} setActive={closeModal} className={cn.modal}>
        {/* @ts-ignore */}
        <ModalBody {...{ ...modal, setModal: closeModal, closeModal }}
          isPaymentWithoutRegistration={isPaymentWithoutRegistration}
          // @ts-ignore
          paymentChildrenId={paymentChildrenId}
        />
      </Modal>
    </>
  )
}

export default MyCourseCard
